/**
 * TeamLogo Component
 */

import React from 'react';
import { TeamLogoProps, TeamLogoTypes, SpotLogoProps } from './teamLogo.types';
import Image from '../Image/image';
import Skeleton from '../Skeleton/skeleton';
import COLORS from '../../style/universal.color';
import { useTheme } from 'styled-components';

const EMPTY_STRING: string = '';

export const TeamLogoFallback: string = 'https://www.mlbstatic.com/team-logos/team-cap-on-light/000.svg';

export const constructTeamLogoURL = (teamId: string | number, teamLogoType: string, darkMode?: boolean): string => {
  const baseUrl = `https://www.mlbstatic.com/team-logos`;
  return teamLogoType === 'team'
    ? `${baseUrl}/${teamId}.svg`
    : `${baseUrl}/team-${teamLogoType}-on-${darkMode ? 'dark' : 'light'}/${teamId}.svg`;
};

export const constructSpotTeamLogo = (teamId?: string | number, spotWidth?: number | string): SpotLogoProps => {
  // only do the math only when spotWidth is a valid number since spotWidth can now be an empty string
  const isNum = typeof spotWidth === 'number';
  const spotsUrl: string = `https://midfield.mlbstatic.com/v1/team/${teamId}/spots/`;

  return {
    '1x': `${spotsUrl}${spotWidth}`,
    '2x': `${spotsUrl}${isNum ? (spotWidth as number) * 2 : spotWidth}`,
    '3x': `${spotsUrl}${isNum ? (spotWidth as number) * 3 : spotWidth}`,
  };
};

const getSpotWidth = (width?: number | string, height?: number | string) => {
  const value = width || height;
  if (typeof value === 'number') {
    return value;
  } else if (typeof value === 'string' && value !== EMPTY_STRING && value?.indexOf('%') === -1) {
    return parseInt(value, 10);
  } else {
    return EMPTY_STRING;
  }
};

const TeamLogo = (props: TeamLogoProps) => {
  const {
    src = EMPTY_STRING,
    teamId,
    teamLogoType = TeamLogoTypes.CAP,
    darkMode = false,
    rounded = false,
    errorImageSrc = TeamLogoFallback,
    backgroundColor = COLORS.transparent,
    border,
    isLoading,
    width,
    height,
    skeletonRounded = false,
    dataAttr,
    alt,
  } = props;
  const theme = useTheme();
  let providedSrc: string = '';
  let providedSrcSet: object | undefined;
  const defaultTestIds: object = {
    'data-mlb-test': 'teamLogos',
    'data-testid': 'teamLogos',
  };

  if (teamLogoType !== TeamLogoTypes.SPOT) {
    providedSrc = teamId ? constructTeamLogoURL(teamId, teamLogoType, darkMode || theme.dark) : src;
  } else {
    const spotWidth = getSpotWidth(width);
    const srcset = constructSpotTeamLogo(teamId, spotWidth);
    providedSrc = srcset['2x'];
    providedSrcSet = srcset;
  }

  if (teamId) {
    if (teamLogoType !== TeamLogoTypes.SPOT) {
      providedSrc = constructTeamLogoURL(teamId, teamLogoType, darkMode || theme.dark);
    } else {
      const spotWidth = getSpotWidth(width, height);
      const srcset = constructSpotTeamLogo(teamId, spotWidth);
      providedSrc = srcset['2x'];
      providedSrcSet = srcset;
    }
  } else {
    providedSrc = src;
  }

  const TeamLogoUI = isLoading ? (
    <Skeleton width={width} height={height || width} isCircle={skeletonRounded || rounded} margin={0} />
  ) : (
    <Image
      {...props}
      key={teamId}
      src={providedSrc}
      rounded={rounded}
      errorImageSrc={errorImageSrc}
      backgroundColor={backgroundColor}
      border={border}
      padding={(rounded && '10%') || undefined}
      dataAttr={{ ...defaultTestIds, ...dataAttr }}
      alt={alt}
      srcset={providedSrcSet}
    />
  );

  return TeamLogoUI;
};

export default TeamLogo;
